import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Stack,
  Divider,
  InputAdornment,
  Menu,
  MenuItem as MenuItemComponent,
  ListItemIcon,
  ListItemText,
  Badge,
  useTheme,
  useMediaQuery,
  Fab
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  MoreVert,
  Person,
  PersonAdd,
  AdminPanelSettings,
  Security,
  CheckCircle,
  RadioButtonUnchecked,
  Circle,
  Email,
  Phone,
  Language,
  Schedule,
  Verified,
  Block,
  Send,
  Refresh,
  Download,
  Upload
} from '@mui/icons-material';
import api from '../services/api';
import { customRolesAPI } from '../services/api';  // Added: 2024-11-12 - User Access Management Module Phase 4.4
import AvatarUpload from '../components/Avatar/AvatarUpload';

interface Agent {
  id: number;
  account_id: number;
  email: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  full_name: string;
  initials: string;
  avatar_url?: string;
  role: 'agent' | 'admin' | 'super_admin';
  status: 'active' | 'inactive' | 'suspended';
  availability_status: 'available' | 'busy' | 'offline';
  auto_offline: boolean;
  confirmed: boolean;
  is_verified: boolean;
  phone_number?: string;
  timezone: string;
  language: string;
  custom_role_id?: number;
  created_at: string;
  updated_at: string;
  // Backward compatibility
  name: string;
  available_name: string;
  thumbnail?: string;
}

interface AgentStats {
  total_agents: number;
  active_agents: number;
  available_agents: number;
  busy_agents: number;
  offline_agents: number;
  confirmed_agents: number;
  unconfirmed_agents: number;
}

const AgentsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [agents, setAgents] = useState<Agent[]>([]);
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [loading, setLoading] = useState(true);
  // Added: 2024-11-12 - User Access Management Module Phase 4.4
  const [customRoles, setCustomRoles] = useState<any[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  
  // Form state
  // Updated: 2024-11-12 - User Access Management Module Phase 4.4 - Added custom_role_id
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    display_name: '',
    phone_number: '',
    role: 'agent',
    custom_role_id: null as number | null,
    timezone: 'UTC',
    language: 'en',
    availability_status: 'available',
    auto_offline: true,
    confirmed: true,
    avatar_url: ''
  });

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAgent, setMenuAgent] = useState<Agent | null>(null);

  useEffect(() => {
    fetchData();
    // Added: 2024-11-12 - User Access Management Module Phase 4.4
    fetchCustomRoles();
  }, [page, searchQuery, roleFilter, statusFilter, availabilityFilter]);

  // Added: 2024-11-12 - User Access Management Module Phase 4.4
  const fetchCustomRoles = async () => {
    try {
      const rolesData = await customRolesAPI.getRoles();
      setCustomRoles(rolesData.filter((r: any) => r.is_active));
    } catch (error) {
      console.error('Error fetching custom roles:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20'
      });

      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (availabilityFilter) params.append('availability_status', availabilityFilter);
      
      // Fetch agents
      const agentsResponse = await api.get(`/agents/?${params.toString()}`);
      setAgents(agentsResponse.data.agents || []);
      setTotal(agentsResponse.data.total || 0);
      setTotalPages(agentsResponse.data.total_pages || 1);
      
      // Fetch stats
      const statsResponse = await api.get('/agents/stats');
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    try {
      await api.post('/agents/', formData);
      setIsCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating agent:', error);
    }
  };

  const handleUpdateAgent = async () => {
    if (!selectedAgent) return;
    
    try {
      await api.patch(`/agents/${selectedAgent.id}`, formData);
      setIsEditDialogOpen(false);
      setSelectedAgent(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error updating agent:', error);
    }
  };

  const handleDeleteAgent = async (agentId: number) => {
    if (window.confirm('Are you sure you want to remove this agent?')) {
      try {
        await api.delete(`/agents/${agentId}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting agent:', error);
      }
    }
  };

  const handleStatusUpdate = async (agentId: number, availability_status: string) => {
    try {
      await api.patch(`/agents/${agentId}/status`, { availability_status });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleResendInvitation = async (agentId: number) => {
    try {
      await api.post(`/agents/${agentId}/invite`);
      // Show success message
    } catch (error) {
      console.error('Error resending invitation:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      display_name: '',
      phone_number: '',
      role: 'agent',
      custom_role_id: null,  // Added: 2024-11-12 - User Access Management Module Phase 4.4
      timezone: 'UTC',
      language: 'en',
      availability_status: 'available',
      auto_offline: true,
      confirmed: true,
      avatar_url: ''
    });
  };

  const openEditDialog = (agent: Agent) => {
    setSelectedAgent(agent);
    setFormData({
      first_name: agent.first_name,
      last_name: agent.last_name,
      email: agent.email,
      password: '',
      display_name: agent.display_name || '',
      phone_number: agent.phone_number || '',
      role: agent.role,
      custom_role_id: agent.custom_role_id || null,  // Added: 2024-11-12 - User Access Management Module Phase 4.4
      timezone: agent.timezone,
      language: agent.language,
      availability_status: agent.availability_status,
      auto_offline: agent.auto_offline,
      confirmed: agent.confirmed,
      avatar_url: agent.avatar_url || ''
    });
    setIsEditDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'busy': return 'warning';
      case 'offline': return 'default';
      default: return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin': return <Security />;
      case 'admin': return <AdminPanelSettings />;
      case 'agent': return <Person />;
      default: return <Person />;
    }
  };

  const getAvailabilityIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircle color="success" />;
      case 'busy': return <Circle color="warning" />;
      case 'offline': return <RadioButtonUnchecked color="disabled" />;
      default: return <Circle color="disabled" />;
    }
  };

  const StatsCards = () => (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary">{stats?.total_agents || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Total Agents</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">{stats?.available_agents || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Available</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">{stats?.busy_agents || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Busy</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="text.secondary">{stats?.offline_agents || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Offline</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const FilterBar = () => (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              label="Role"
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="agent">Agent</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="super_admin">Super Admin</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Availability</InputLabel>
            <Select
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              label="Availability"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="busy">Busy</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            fullWidth
          >
            Refresh
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const AgentCard = ({ agent }: { agent: Agent }) => (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        avatar={
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={getAvailabilityIcon(agent.availability_status)}
          >
            <Avatar
              src={agent.avatar_url}
              sx={{ width: 56, height: 56 }}
            >
              {agent.initials}
            </Avatar>
          </Badge>
        }
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">{agent.available_name}</Typography>
            {agent.is_verified && <Verified color="primary" fontSize="small" />}
            {!agent.confirmed && <Block color="warning" fontSize="small" />}
          </Stack>
        }
        subheader={
          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {getRoleIcon(agent.role)}
              <Typography variant="body2" color="text.secondary">
                {agent.role.replace('_', ' ').toUpperCase()}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {agent.email}
            </Typography>
          </Stack>
        }
        action={
          <IconButton
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              setMenuAgent(agent);
            }}
          >
            <MoreVert />
          </IconButton>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              icon={getAvailabilityIcon(agent.availability_status)}
              label={agent.availability_status.toUpperCase()}
              color={getStatusColor(agent.availability_status) as any}
              size="small"
            />
            <Chip
              label={agent.status.toUpperCase()}
              color={agent.status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Stack>
          
          {agent.phone_number && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Phone fontSize="small" color="action" />
              <Typography variant="body2">{agent.phone_number}</Typography>
            </Stack>
          )}
          
          <Stack direction="row" alignItems="center" spacing={1}>
            <Language fontSize="small" color="action" />
            <Typography variant="body2">{agent.language.toUpperCase()}</Typography>
          </Stack>
          
          <Stack direction="row" alignItems="center" spacing={1}>
            <Schedule fontSize="small" color="action" />
            <Typography variant="body2">{agent.timezone}</Typography>
          </Stack>
        </Stack>
      </CardContent>
      <CardActions>
        <Button
          size="small"
          startIcon={<Edit />}
          onClick={() => openEditDialog(agent)}
        >
          Edit
        </Button>
        {!agent.confirmed && (
          <Button
            size="small"
            startIcon={<Send />}
            onClick={() => handleResendInvitation(agent.id)}
          >
            Resend Invite
          </Button>
        )}
      </CardActions>
    </Card>
  );

  const AgentTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Agent</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Availability</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agents.map((agent) => (
            <TableRow key={agent.id}>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={getAvailabilityIcon(agent.availability_status)}
                  >
                    <Avatar src={agent.avatar_url}>{agent.initials}</Avatar>
                  </Badge>
                  <Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">{agent.available_name}</Typography>
                      {agent.is_verified && <Verified color="primary" fontSize="small" />}
                      {!agent.confirmed && <Block color="warning" fontSize="small" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {agent.email}
                    </Typography>
                  </Stack>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {getRoleIcon(agent.role)}
                  <Typography variant="body2">
                    {agent.role.replace('_', ' ').toUpperCase()}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Chip
                  label={agent.status.toUpperCase()}
                  color={agent.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  icon={getAvailabilityIcon(agent.availability_status)}
                  label={agent.availability_status.toUpperCase()}
                  color={getStatusColor(agent.availability_status) as any}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Stack>
                  {agent.phone_number && (
                    <Typography variant="body2">{agent.phone_number}</Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {agent.timezone} • {agent.language.toUpperCase()}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <IconButton
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setMenuAgent(agent);
                  }}
                >
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2
      }}>
        <Typography variant="h4" component="h1">
          Agents
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setIsCreateDialogOpen(true)}
          size={isMobile ? "small" : "medium"}
        >
          Add Agent
        </Button>
      </Box>

      {/* Stats Cards */}
      <StatsCards />

      {/* Filter Bar */}
      <FilterBar />

      {/* View Toggle */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="Card View" />
          <Tab label="Table View" />
        </Tabs>
      </Box>

      {/* Content */}
      {currentTab === 0 ? (
        <Grid container spacing={3}>
          {agents.map((agent) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={agent.id}>
              <AgentCard agent={agent} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <AgentTable />
      )}

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </Button>
        <Typography sx={{ mx: 2, alignSelf: 'center' }}>
          Page {page} of {totalPages}
        </Typography>
        <Button
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </Box>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          <Add />
        </Fab>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItemComponent onClick={() => {
          if (menuAgent) openEditDialog(menuAgent);
          setAnchorEl(null);
        }}>
          <ListItemIcon><Edit /></ListItemIcon>
          <ListItemText>Edit Agent</ListItemText>
        </MenuItemComponent>
        
        {menuAgent && !menuAgent.confirmed && (
          <MenuItemComponent onClick={() => {
            if (menuAgent) handleResendInvitation(menuAgent.id);
            setAnchorEl(null);
          }}>
            <ListItemIcon><Send /></ListItemIcon>
            <ListItemText>Resend Invitation</ListItemText>
          </MenuItemComponent>
        )}
        
        <Divider />
        
        <MenuItemComponent onClick={() => {
          if (menuAgent) handleStatusUpdate(menuAgent.id, 'available');
          setAnchorEl(null);
        }}>
          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
          <ListItemText>Set Available</ListItemText>
        </MenuItemComponent>
        
        <MenuItemComponent onClick={() => {
          if (menuAgent) handleStatusUpdate(menuAgent.id, 'busy');
          setAnchorEl(null);
        }}>
          <ListItemIcon><Circle color="warning" /></ListItemIcon>
          <ListItemText>Set Busy</ListItemText>
        </MenuItemComponent>
        
        <MenuItemComponent onClick={() => {
          if (menuAgent) handleStatusUpdate(menuAgent.id, 'offline');
          setAnchorEl(null);
        }}>
          <ListItemIcon><RadioButtonUnchecked color="disabled" /></ListItemIcon>
          <ListItemText>Set Offline</ListItemText>
        </MenuItemComponent>
        
        <Divider />
        
        <MenuItemComponent 
          onClick={() => {
            if (menuAgent) handleDeleteAgent(menuAgent.id);
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><Delete color="error" /></ListItemIcon>
          <ListItemText>Remove Agent</ListItemText>
        </MenuItemComponent>
      </Menu>

      {/* Create Agent Dialog */}
      <Dialog 
        open={isCreateDialogOpen} 
        onClose={() => setIsCreateDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Add New Agent</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Avatar Upload */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <AvatarUpload
                currentAvatar={formData.avatar_url}
                onAvatarChange={(url) => setFormData(prev => ({ ...prev, avatar_url: url || '' }))}
                name={`${formData.first_name} ${formData.last_name}`.trim()}
                size={120}
              />
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Display Name (Optional)"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  value={formData.phone_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    label="Role"
                  >
                    <MenuItem value="agent">Agent</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="super_admin">Super Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Added: 2024-11-12 - User Access Management Module Phase 4.4 */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Custom Role (Optional)</InputLabel>
                  <Select
                    value={formData.custom_role_id || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_role_id: e.target.value ? Number(e.target.value) : null }))}
                    label="Custom Role (Optional)"
                  >
                    <MenuItem value="">
                      <em>None - Use Base Role</em>
                    </MenuItem>
                    {customRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name} ({role.permission_count} permissions)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={formData.timezone}
                    onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                    label="Timezone"
                  >
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="America/New_York">Eastern Time</MenuItem>
                    <MenuItem value="America/Chicago">Central Time</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={formData.language}
                    onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                    label="Language"
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.auto_offline}
                      onChange={(e) => setFormData(prev => ({ ...prev, auto_offline: e.target.checked }))}
                    />
                  }
                  label="Auto Offline"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateAgent} variant="contained">
            Add Agent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Agent Dialog */}
      <Dialog 
        open={isEditDialogOpen} 
        onClose={() => setIsEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Edit Agent</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Avatar Upload */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <AvatarUpload
                currentAvatar={formData.avatar_url}
                onAvatarChange={(url) => setFormData(prev => ({ ...prev, avatar_url: url || '' }))}
                name={`${formData.first_name} ${formData.last_name}`.trim()}
                size={120}
              />
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Display Name (Optional)"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  value={formData.phone_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    label="Role"
                  >
                    <MenuItem value="agent">Agent</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="super_admin">Super Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Added: 2024-11-12 - User Access Management Module Phase 4.4 */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Custom Role (Optional)</InputLabel>
                  <Select
                    value={formData.custom_role_id || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_role_id: e.target.value ? Number(e.target.value) : null }))}
                    label="Custom Role (Optional)"
                  >
                    <MenuItem value="">
                      <em>None - Use Base Role</em>
                    </MenuItem>
                    {customRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name} ({role.permission_count} permissions)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Availability</InputLabel>
                  <Select
                    value={formData.availability_status}
                    onChange={(e) => setFormData(prev => ({ ...prev, availability_status: e.target.value }))}
                    label="Availability"
                  >
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="busy">Busy</MenuItem>
                    <MenuItem value="offline">Offline</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={formData.timezone}
                    onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                    label="Timezone"
                  >
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="America/New_York">Eastern Time</MenuItem>
                    <MenuItem value="America/Chicago">Central Time</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.auto_offline}
                      onChange={(e) => setFormData(prev => ({ ...prev, auto_offline: e.target.checked }))}
                    />
                  }
                  label="Auto Offline"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateAgent} variant="contained">
            Update Agent
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentsPage;